import { useRef, useState } from 'react';
import { darken, lighten } from 'polished';
import styled from 'styled-components';

import { CheckeredMargin, gridColorSelector } from '@/components/v2/GridLines';
import {
  LayoutGridCondensed,
  layoutGridCondensedPadding,
  LayoutSubgrid
} from '@/components/v2/Page/LayoutGrid';
import { styles as headingStyles } from '@/components/v2/UtilityCss/Heading';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import {
  HEX_DEMO_VIDEO_WHAT_IS_HEX,
  INTERNAL_LINKS,
  SIGNUP_LINK,
  TRACKING_LABELS
} from '@/utils';

import { FullScreenVideo } from '../global/FullScreenVideo';
import { generateFluidFontSize } from '../v2/UtilityCss/utils';

import { CartesianGrid } from './CartesianGrid';
import {
  CircleAccents,
  CornerAccents,
  PrimaryCta,
  PrimaryCtaIconWrapper,
  SecondaryCta
} from './Cta';
import { CtaIcon, type CtaIconHandle } from './CtaIcon';
import { CustomerBanner } from './CustomerBanner';
import { DotGridMask } from './Textures';
import { Thumbnail } from './Thumbnail';

const Bg = styled(LayoutSubgrid)`
  position: relative;
  overflow-x: clip;
`;

const Wrapper = styled(LayoutGridCondensed)`
  position: relative;
`;

const ThumbnailShadow = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  aspect-ratio: 639 / 385;
  background-color: ${({ theme }) => theme.palette.fg100};
  border-radius: 4px;
  overflow: clip;
  box-shadow:
    0px 0px 1px rgba(0, 0, 0, 0.64),
    0px 1px 1px rgba(0, 0, 0, 0.56),
    0px 2px 1px rgba(0, 0, 0, 0.48),
    0px 4px 2px rgba(0, 0, 0, 0.32),
    0px 6px 2px rgba(0, 0, 0, 0.16);

  & > * {
    grid-area: 1 / -1;
  }

  img {
    object-fit: cover;
    width: 100%;
  }
`;

const ContentThumbnail = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 64px;
  min-height: calc(100svh - 96px);
  min-height: calc(100vh - 96px);
  padding: 0 ${layoutGridCondensedPadding};
  padding-top: 80px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    padding-top: 240px;
    padding-bottom: 80px;
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    padding: 0;
    padding-top: 180px;
    padding-bottom: 48px;
    gap: 32px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const Text = styled.div`
  grid-row: 1;
  grid-column: 1;
  align-self: center;
  padding: 0 0 40px 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    padding-left: 0;
  }
`;

const H1 = styled.h1`
  position: relative;
  font-size: ${generateFluidFontSize(38, 72, { maxWidth: 1708 })};
  background-size: 100%;
  background-repeat: repeat;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;

  ${({ theme }) => theme.gradientText.DEFAULT}

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    text-align: center;
  }
`;

const P = styled.p`
  text-wrap: balance;
  font-size: ${generateFluidFontSize(16, 24, {
    minWidth: 800,
    maxWidth: 1708
  })};
  max-width: 40ch;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    text-align: center;
  }
`;

const Actions = styled.div`
  grid-row: 2;
  grid-column: 1;
  padding-right: 0;
  display: flex;
  height: min-content;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    padding-left: 0;
    gap: 12px;
    justify-content: center;
  }
`;

const DotGridBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.palette.fgA700};
`;

const PerlinNoise = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(/images/home-2025/textures/noise-rose-128x128.png);
  background-size: 512px 512px;

  animation: noise 8s linear infinite;
  animation-play-state: paused;
  opacity: 0;

  @keyframes noise {
    from {
      background-position: -512px -512px;
    }
    to {
      background-position: 0px 0px;
    }
  }
`;

const FeatherEdgeMask = styled.div`
  position: absolute;
  inset: 0;
  --fall-off: 16px;
  mask-image: linear-gradient(
      to right,
      transparent,
      white var(--fall-off),
      white calc(100% - var(--fall-off)),
      transparent
    ),
    linear-gradient(
      to bottom,
      white,
      transparent var(--fall-off),
      transparent calc(100% - var(--fall-off)),
      white
    );
  mask-composite: subtract;
`;

const PegBoard = styled.button`
  --hover-transition-duration: 529.33ms;
  --hover-transition-timing-function: linear(
    0 0%,
    0.005892 1%,
    0.022227 2%,
    0.047151 3%,
    0.079006 4%,
    0.116321 5%,
    0.157797 6%,
    0.202293 7.000000000000001%,
    0.248818 8%,
    0.296516 9%,
    0.344652 10%,
    0.392606 11%,
    0.439857 12%,
    0.485977 13%,
    0.530617 14.000000000000002%,
    0.573504 15%,
    0.614426 16%,
    0.653228 17%,
    0.689804 18%,
    0.724091 19%,
    0.756062 20%,
    0.785721 21%,
    0.813097 22%,
    0.838242 23%,
    0.861223 24%,
    0.882123 25%,
    0.901035 26%,
    0.918061 27%,
    0.933305 28.000000000000004%,
    0.946879 28.999999999999996%,
    0.958894 30%,
    0.96946 31%,
    0.978689 32%,
    0.986687 33%,
    0.993561 34%,
    0.999411 35%,
    1.004333 36%,
    1.00842 37%,
    1.011758 38%,
    1.014427 39%,
    1.016506 40%,
    1.018063 41%,
    1.019165 42%,
    1.019871 43%,
    1.020237 44%,
    1.020313 45%,
    1.020144 46%,
    1.019771 47%,
    1.019232 48%,
    1.018558 49%,
    1.01778 50%,
    1.016922 51%,
    1.016007 52%,
    1.015055 53%,
    1.014083 54%,
    1.013105 55.00000000000001%,
    1.012133 56.00000000000001%,
    1.011177 56.99999999999999%,
    1.010246 57.99999999999999%,
    1.009346 59%,
    1.008483 60%,
    1.00766 61%,
    1.006882 62%,
    1.006148 63%,
    1.005462 64%,
    1.004822 65%,
    1.00423 66%,
    1.003683 67%,
    1.003182 68%,
    1.002725 69%,
    1.002309 70%,
    1.001933 71%,
    1.001595 72%,
    1.001293 73%,
    1.001024 74%,
    1.000786 75%,
    1.000578 76%,
    1.000396 77%,
    1.000238 78%,
    1.000103 79%,
    0.999989 80%
  );

  position: relative;
  padding: 16px;
  background-color: ${({ theme }) => darken(0.0, theme.palette.fg100)};
  cursor: pointer;
  height: fit-content;

  border: 1px solid ${({ theme }) => lighten(0.125, theme.palette.fg100)};
  box-shadow:
    1px 2px 4px rgba(0, 0, 0, 0.2),
    4px 6px 8px rgba(0, 0, 0, 0.17),
    10px 14px 10px rgba(0, 0, 0, 0.1),
    18px 25px 12px rgba(0, 0, 0, 0.03),
    28px 39px 13px rgba(0, 0, 0, 0);
  border-radius: 8px;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};

  &::after {
    content: ' ';
    border-radius: inherit;
    position: absolute;
    inset: 0;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  }

  & {
    transition: transform, border;
    transition-duration: var(--hover-transition-duration);
    transition-timing-function: var(--hover-transition-timing-function);
  }

  ${ThumbnailShadow} {
    transition: transform;
    transition-duration: var(--hover-transition-duration);
    transition-timing-function: var(--hover-transition-timing-function);
  }

  ${PerlinNoise} {
    transition: opacity;
    transition-duration: var(--hover-transition-duration);
    transition-timing-function: var(--hover-transition-timing-function);
  }

  @media (hover: hover) {
    &:hover,
    &:focus-visible {
      --play-indicator-state: running;
      --indicator-transform: scale(1.1);
      transform: translateY(-4px);
      border: 1px solid ${({ theme }) => theme.palette.fgA900};

      ${ThumbnailShadow} {
        transform-origin: center center;
        transform: scale(0.986);
      }

      ${PerlinNoise} {
        opacity: 1;
        animation-play-state: running;
      }
    }
  }
`;

const ThumbnailArea = styled.div`
  display: grid;
  place-items: stretch;
  flex-grow: 1;
  max-width: 705px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin: 0 auto;
  }
`;

const SparkleSvg = styled.svg`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(70%, -28%) rotate(-90deg);
  width: 40px;
  color: ${({ theme }) => theme.palette.fg1000};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

function Sparkle() {
  return (
    <SparkleSvg
      fill="none"
      viewBox="0 0 47 47"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.96094 23.0768L1.02019 31.0175"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1"
      />
      <path
        d="M16.0223 26.0006L16.0223 37.2294"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1"
      />
      <path
        d="M23.0788 23.0793L31.0195 31.0201"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1"
      />
      <path
        d="M26.0016 16.0196L37.2305 16.0196"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1"
      />
      <path
        d="M23.0788 8.96006L31.0195 1.01932"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1"
      />
    </SparkleSvg>
  );
}

export function Hero() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const ctaIconRef = useRef<CtaIconHandle>(null!);

  return (
    <Bg>
      <CheckeredMargin />
      <LayoutSubgrid data-content-grid>
        <Wrapper>
          <CartesianGrid />
          <ContentThumbnail>
            <Content>
              <Text>
                <H1 className={headingStyles.h0}>
                  <Sparkle />
                  Go deeper than
                  <br /> dashboards
                </H1>
                <P className={textStyles.xl}>
                  Hex is the AI-powered analytics workspace built for teams
                  driving faster answers and better decisions.
                </P>
              </Text>
              <Actions>
                <div
                  onMouseEnter={() => {
                    ctaIconRef.current.startAnimation();
                  }}
                  onMouseLeave={() => ctaIconRef.current.stopAnimation()}>
                  <CircleAccents>
                    <CornerAccents>
                      <PrimaryCta
                        className={textStyles.l}
                        to={`${SIGNUP_LINK}?source=hero`}>
                        <PrimaryCtaIconWrapper>
                          <CtaIcon ref={ctaIconRef} />
                        </PrimaryCtaIconWrapper>
                        Get started
                      </PrimaryCta>
                    </CornerAccents>
                  </CircleAccents>
                </div>

                <CornerAccents>
                  <SecondaryCta
                    className={textStyles.l}
                    to={INTERNAL_LINKS.RequestADemo}>
                    Request a demo
                  </SecondaryCta>
                </CornerAccents>
              </Actions>
            </Content>

            <ThumbnailArea>
              <PegBoard onClick={() => setIsVideoOpen(true)}>
                <FeatherEdgeMask>
                  <DotGridMask>
                    <DotGridBg />
                    <PerlinNoise />
                  </DotGridMask>
                </FeatherEdgeMask>
                <ThumbnailShadow>
                  <Thumbnail />
                </ThumbnailShadow>
              </PegBoard>
            </ThumbnailArea>
          </ContentThumbnail>
        </Wrapper>
        <CustomerBanner />
      </LayoutSubgrid>
      <CheckeredMargin data-right />
      <FullScreenVideo
        isOpen={isVideoOpen}
        path={HEX_DEMO_VIDEO_WHAT_IS_HEX}
        trackingLabel={TRACKING_LABELS.VideoPlayed}
        onClose={() => setIsVideoOpen(false)}
      />
    </Bg>
  );
}
